import React from "react";
import {
  Badge,
  Box,
  Container,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Text,
  Tooltip,
  VStack,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaShieldAlt, FaGlobe, FaBlog, FaBriefcase, FaLock } from "react-icons/fa";
import { MdPolicy, MdDashboard, MdPeople, MdQueryStats } from "react-icons/md";
import { TbReport } from "react-icons/tb";
import { IoDocumentTextOutline, IoAlertSharp } from "react-icons/io5";
import { BsArrowRight, BsMoon, BsSun } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

const ModuleSelection = () => {
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();

  // Get user type from localStorage
  // type 1 → only Insugo Admin (Card 1) accessible
  // type 2 → only Insugo Digi Admin (Card 2) accessible
  // type 0 → both accessible
  const loggedInUser = localStorage.getItem("userId");
  const user = loggedInUser ? JSON.parse(loggedInUser) : null;
  const userType = user?.type !== undefined ? Number(user.type) : 0;

  const canAccessAdmin = userType === 0 || userType === 1;
  const canAccessDigi  = userType === 0 || userType === 2;

  // Color tokens
  const pageBg    = useColorModeValue("#f0f4f8", "gray.900");
  const headerBg  = useColorModeValue("white", "gray.800");
  const cardBg    = useColorModeValue("white", "gray.800");
  const headingClr = useColorModeValue("gray.700", "whiteAlpha.900");
  const subClr    = useColorModeValue("gray.500", "gray.400");
  const bodyText  = useColorModeValue("gray.600", "gray.300");
  const featureClr = useColorModeValue("gray.700", "gray.200");
  const borderClr = useColorModeValue("gray.100", "whiteAlpha.100");
  const iconClr   = useColorModeValue("gray.600", "gray.300");
  const logoBg      = useColorModeValue("#727b82", "gray.600");
  const disabledBg  = useColorModeValue("gray.100", "gray.700");
  const disabledBdr = useColorModeValue("gray.200", "gray.600");

  const cards = [
    {
      gradient: canAccessAdmin ? "linear(135deg, #FF6B6B, #FF8E53)" : "linear(135deg, #a0aec0, #718096)",
      badge: "Admin",
      icon: <FaShieldAlt size={26} />,
      title: "Insugo Corporate Website Admin",
      subtitle: "Insurance management portal",
      description:
        "Manage insurance policies, track claims, handle users, and oversee all administrative operations.",
      features: [
        { icon: <TbReport size={17} color="#FF6B6B" />, label: "Claims Management" },
        { icon: <MdPeople size={17} color="#FF6B6B" />, label: "User Management" },
        { icon: <MdPolicy size={17} color="#FF6B6B" />, label: "Policy Management" },
        { icon: <MdQueryStats size={17} color="#FF6B6B" />, label: "Analytics & Reports" },
        { icon: <IoAlertSharp size={17} color="#FF6B6B" />, label: "Alerts & Notifications" },
      ],
      cta: "Open Insugo Admin",
      ctaColor: "#FF6B6B",
      onClick: canAccessAdmin ? () => navigate("/") : null,
      disabled: !canAccessAdmin,
    },
    {
      gradient: canAccessDigi ? "linear(135deg, #5B86E5, #36D1DC)" : "linear(135deg, #a0aec0, #718096)",
      badge: "Digi Admin",
      icon: <FaGlobe size={26} />,
      title: "Insugo Digi Admin",
      subtitle: "Website content management",
      description:
        "Manage website content, blogs, careers, testimonials, media, and digital presence for Insugo.",
      features: [
        { icon: <MdDashboard size={17} color="#5B86E5" />, label: "Policy & Associate Management" },
        { icon: <FaBlog size={17} color="#5B86E5" />, label: "Blog & Content Management" },
        { icon: <FaBriefcase size={17} color="#5B86E5" />, label: "Careers & Queries" },
        { icon: <IoDocumentTextOutline size={17} color="#5B86E5" />, label: "Testimonials & Media" },
      ],
      cta: "Open Insugo Digi Admin",
      ctaColor: "#5B86E5",
      onClick: canAccessDigi ? () => navigate("/digi") : null,
      disabled: !canAccessDigi,
    },
  ];

  return (
    <Box minH="100vh" bg={pageBg}>
      {/* ── Top bar ── */}
      <Flex
        px={{ base: 4, md: 8 }}
        py={3}
        bg={headerBg}
        shadow="sm"
        align="center"
        justify="space-between"
        borderBottom={`1px solid ${borderClr}`}
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Image
          src="/logo.png"
          h={{ base: "32px", md: "40px" }}
          alt="Insugo"
          fallbackSrc="/Logo1.png"
          bg={logoBg}
          borderRadius={3}
          p={1}
        />

        <Flex align="center" gap={2}>
          <Text
            color={subClr}
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="medium"
            display={{ base: "none", sm: "block" }}
          >
            Select a workspace
          </Text>
          <Tooltip
            label={colorMode === "dark" ? "Light mode" : "Dark mode"}
            hasArrow
          >
            <IconButton
              aria-label="Toggle color mode"
              icon={
                colorMode === "dark" ? <BsSun size={16} /> : <BsMoon size={16} />
              }
              onClick={toggleColorMode}
              variant="ghost"
              size="sm"
              color={iconClr}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* ── Main content ── */}
      <Container maxW="container.xl" py={{ base: 10, md: 16 }} px={{ base: 4, md: 8 }}>
        <Heading
          textAlign="center"
          mb={2}
          fontSize={{ base: "xl", md: "2xl" }}
          color={headingClr}
          fontWeight="bold"
        >
          Insugo Workspace
        </Heading>
        <Text
          textAlign="center"
          color={subClr}
          mb={{ base: 10, md: 14 }}
          fontSize={{ base: "sm", md: "md" }}
        >
          Choose the admin panel you want to access
        </Text>

        {/* ── Cards ── */}
        <Flex
          gap={{ base: 6, md: 10 }}
          justify="center"
          flexWrap="wrap"
          align="stretch"
        >
          {cards.map((card) => (
            <Box
              key={card.title}
              w={{ base: "100%", sm: "calc(50% - 24px)", lg: "370px" }}
              borderRadius="2xl"
              overflow="hidden"
              shadow={card.disabled ? "sm" : "lg"}
              cursor={card.disabled ? "not-allowed" : "pointer"}
              bg={card.disabled ? disabledBg : cardBg}
              onClick={card.disabled ? undefined : card.onClick}
              transition="all 0.22s ease"
              opacity={card.disabled ? 0.55 : 1}
              _hover={card.disabled ? {} : { transform: "translateY(-6px)", shadow: "2xl" }}
              border={`1px solid ${card.disabled ? disabledBdr : borderClr}`}
              position="relative"
            >
              {/* Lock badge for disabled cards */}
              {card.disabled && (
                <Flex
                  position="absolute"
                  top={3}
                  right={3}
                  bg="blackAlpha.600"
                  borderRadius="full"
                  p={2}
                  align="center"
                  gap={1}
                  zIndex={2}
                >
                  <FaLock size={11} color="#fff" />
                  <Text fontSize="xs" color="white" fontWeight="semibold">
                    No Access
                  </Text>
                </Flex>
              )}

              {/* Gradient header */}
              <Box bgGradient={card.gradient} p={6} color="white">
                <Badge
                  bg="rgba(255,255,255,0.22)"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  letterSpacing="wide"
                  mb={5}
                >
                  {card.badge}
                </Badge>
                <Flex align="center" gap={4}>
                  <Box bg="rgba(255,255,255,0.18)" p={3} borderRadius="xl">
                    {card.icon}
                  </Box>
                  <Box>
                    <Heading size="md" fontWeight="bold">
                      {card.title}
                    </Heading>
                    <Text fontSize="sm" opacity={0.88} mt={0.5}>
                      {card.subtitle}
                    </Text>
                  </Box>
                </Flex>
              </Box>

              {/* Card body */}
              <Box p={6}>
                <Text
                  color={bodyText}
                  fontSize="sm"
                  mb={5}
                  lineHeight="tall"
                >
                  {card.description}
                </Text>

                <VStack align="start" spacing={3} mb={7}>
                  {card.features.map((f) => (
                    <HStack key={f.label} spacing={3}>
                      {f.icon}
                      <Text fontSize="sm" color={featureClr}>
                        {f.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                <Flex
                  align="center"
                  gap={2}
                  color={card.disabled ? "gray.400" : card.ctaColor}
                  fontWeight="semibold"
                  fontSize="sm"
                >
                  <Text>{card.disabled ? "Access Restricted" : card.cta}</Text>
                  {card.disabled ? <FaLock size={13} /> : <BsArrowRight />}
                </Flex>
              </Box>
            </Box>
          ))}
        </Flex>
      </Container>

      <Text textAlign="center" color={subClr} fontSize="xs" pb={8}>
        © {new Date().getFullYear()} Insugo — All rights reserved
      </Text>
    </Box>
  );
};

export default ModuleSelection;
